// Story 11.1 — reducer puro para los tabs del editor. Aislado de Zustand
// para tests rápidos. La store wrapea estos helpers.

export interface EditorTab {
  path: string;
  content: string;
  /** Contenido al momento de abrir/guardar — comparar contra `content` para detectar dirty. */
  baseline: string;
  lastAccessed: number;
  /** ext/*.ts en MVP es readonly. */
  readOnly: boolean;
}

export interface EditorTabsState {
  tabs: EditorTab[];
  activePath: string | null;
}

export const MAX_TABS = 10;

/** True si el tab tiene cambios sin guardar. */
export function isDirty(tab: EditorTab): boolean {
  return tab.content !== tab.baseline;
}

export const initialEditorTabsState: EditorTabsState = {
  tabs: [],
  activePath: null,
};

export type EditorAction =
  | {
      type: 'open';
      path: string;
      content: string;
      readOnly: boolean;
      now: number;
    }
  | { type: 'close'; path: string }
  | { type: 'set-active'; path: string; now: number }
  | { type: 'update-content'; path: string; content: string }
  | { type: 'mark-saved'; path: string }
  | { type: 'close-others'; path: string }
  | { type: 'close-all' };

export function editorTabsReducer(state: EditorTabsState, action: EditorAction): EditorTabsState {
  switch (action.type) {
    case 'open':
      return openTab(state, action);
    case 'close':
      return closeTab(state, action.path);
    case 'set-active': {
      if (!state.tabs.find((t) => t.path === action.path)) return state;
      return {
        activePath: action.path,
        tabs: state.tabs.map((t) =>
          t.path === action.path ? { ...t, lastAccessed: action.now } : t,
        ),
      };
    }
    case 'update-content': {
      const next = state.tabs.map((t) =>
        t.path === action.path ? { ...t, content: action.content } : t,
      );
      return { ...state, tabs: next };
    }
    case 'mark-saved': {
      const next = state.tabs.map((t) =>
        t.path === action.path ? { ...t, baseline: t.content } : t,
      );
      return { ...state, tabs: next };
    }
    case 'close-others': {
      const keep = state.tabs.find((t) => t.path === action.path);
      if (!keep) return state;
      return { tabs: [keep], activePath: action.path };
    }
    case 'close-all':
      return initialEditorTabsState;
    default:
      return state;
  }
}

function openTab(
  state: EditorTabsState,
  action: Extract<EditorAction, { type: 'open' }>,
): EditorTabsState {
  const existing = state.tabs.find((t) => t.path === action.path);
  if (existing) {
    // Solo actualizamos lastAccessed; el contenido del tab ya abierto
    // *gana* (puede tener edits dirty). Si el caller necesita refresh,
    // debería cerrar primero.
    return {
      activePath: action.path,
      tabs: state.tabs.map((t) =>
        t.path === action.path ? { ...t, lastAccessed: action.now } : t,
      ),
    };
  }

  const newTab: EditorTab = {
    path: action.path,
    content: action.content,
    baseline: action.content,
    lastAccessed: action.now,
    readOnly: action.readOnly,
  };

  // LRU eviction si excedemos MAX_TABS. Selección de víctima ocurre sobre
  // los tabs *existentes* (excluye al newcomer — no tiene sentido evict el
  // tab que acabamos de abrir). Preferimos descartar uno *no dirty*; si
  // todos están dirty, descartamos el más viejo igualmente — el caller debe
  // avisar al user antes (UX layer, no acá).
  let prevTabs = state.tabs;
  if (prevTabs.length >= MAX_TABS) {
    const cleanCandidates = prevTabs.filter((t) => !isDirty(t));
    const pool = cleanCandidates.length > 0 ? cleanCandidates : prevTabs;
    const victim = pool.reduce((a, b) => (a.lastAccessed < b.lastAccessed ? a : b));
    prevTabs = prevTabs.filter((t) => t.path !== victim.path);
  }

  return { tabs: [...prevTabs, newTab], activePath: action.path };
}

function closeTab(state: EditorTabsState, path: string): EditorTabsState {
  const tabs = state.tabs.filter((t) => t.path !== path);
  if (tabs.length === state.tabs.length) return state;
  let activePath = state.activePath;
  if (activePath === path) {
    // Activamos la tab más recientemente accedida que sobrevivió.
    if (tabs.length === 0) {
      activePath = null;
    } else {
      const next = tabs.reduce((a, b) => (a.lastAccessed > b.lastAccessed ? a : b));
      activePath = next.path;
    }
  }
  return { tabs, activePath };
}
